/**
 * Text Parser — Clipboard-to-Flyer Smart Import
 *
 * Extracts structured event information from unstructured text using a
 * cascade of regex pattern matching and positional heuristics.
 * Runs entirely client-side — no API calls, no cost.
 *
 * Pipeline:
 *   Raw text -> strip HTML -> Layer 1 (regex patterns) -> Layer 2 (heuristics)
 *   -> structured fields with confidence scores
 *
 * @module text-parser
 */

// ─────────────────────────────────────────────────────────────────────────────
// HTML STRIPPING
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Strip HTML tags and decode entities from pasted text.
 * Handles clipboard content that may contain HTML from web copy.
 */
function stripHtml(text) {
  if (!text) return '';
  return text
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim();
}

// ─────────────────────────────────────────────────────────────────────────────
// LAYER 1: REGEX PATTERN EXTRACTION
// ─────────────────────────────────────────────────────────────────────────────

const MONTHS = 'jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?';
const DAYS_OF_WEEK = 'mon(?:day)?|tue(?:s(?:day)?)?|wed(?:nesday)?|thu(?:rs(?:day)?)?|fri(?:day)?|sat(?:urday)?|sun(?:day)?';

// Date patterns — ordered from most specific to least
const DATE_PATTERNS = [
  // "Saturday, May 10th, 2026" / "Sat May 10 2026"
  new RegExp(`((?:${DAYS_OF_WEEK}),?\\s+)?(${MONTHS})\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?,?\\s*(\\d{4})?`, 'gi'),
  // "May 10th" / "May 10"
  new RegExp(`(${MONTHS})\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:,?\\s*(\\d{4}))?`, 'gi'),
  // "10 May 2026" / "10th May"
  new RegExp(`(\\d{1,2})(?:st|nd|rd|th)?\\s+(${MONTHS})\\.?,?\\s*(\\d{4})?`, 'gi'),
  // "Saturday, 5/10/2026" / "5/10/26" / "5-10-2026"
  new RegExp(`(?:(?:${DAYS_OF_WEEK}),?\\s+)?(\\d{1,2})[/\\-](\\d{1,2})[/\\-](\\d{2,4})`, 'gi'),
  // "Saturday May 10" (day-of-week + month + day, no year)
  new RegExp(`(${DAYS_OF_WEEK}),?\\s+(${MONTHS})\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?`, 'gi'),
  // "5/10" — only if preceded by "on" or day-of-week
  new RegExp(`(?:on\\s+|(?:${DAYS_OF_WEEK}),?\\s+)(\\d{1,2})[/\\-](\\d{1,2})`, 'gi'),
];

// Time patterns
const TIME_PATTERNS = [
  // "2pm - 6pm" / "2:00 PM - 6:00 PM" / "2pm to 6pm"
  /(\d{1,2}(?::\d{2})?\s*(?:am|pm))\s*(?:[-\u2013\u2014~]|to|until|til)\s*(\d{1,2}(?::\d{2})?\s*(?:am|pm))/gi,
  // "2-6pm" / "2 - 6 pm"
  /(\d{1,2}(?::\d{2})?)\s*(?:[-\u2013\u2014~]|to|until|til)\s*(\d{1,2}(?::\d{2})?)\s*(am|pm)/gi,
  // "doors at 9pm" / "starts at 7:30pm"
  /(?:doors|starts?|begins?|show(?:time)?|opens?)\s+(?:at\s+)?(\d{1,2}(?::\d{2})?\s*(?:am|pm)?)/gi,
  // Standalone time: "9PM" / "7:30 PM"
  /\b(\d{1,2}:\d{2}\s*(?:am|pm))\b/gi,
  /\b(\d{1,2}\s*(?:am|pm))\b/gi,
];

// Price patterns
const PRICE_PATTERNS = [
  // "$15 advance / $20 door" / "$10 ADV / $15 DOOR"
  /\$\d+(?:\.\d{2})?\s*(?:adv(?:ance)?|pre[-\s]?sale)?\s*[/|&]\s*\$\d+(?:\.\d{2})?\s*(?:door|dos|gate)?/gi,
  // "$5-$25" / "$5 - $25"
  /\$\d+(?:\.\d{2})?\s*[-\u2013\u2014]\s*\$\d+(?:\.\d{2})?/gi,
  // "$15" / "$5.00"
  /\$\d+(?:\.\d{2})?(?:\s+(?:adv(?:ance)?|door|cover|entry|admission|suggested\s+donation|donation))?/gi,
  // "free" / "no cover" / "free admission"
  /\b(?:free(?:\s+(?:admission|entry|event))?|no\s+cover|no\s+charge|complimentary|pwyc|pay\s+what\s+you\s+(?:can|want))\b/gi,
  // "sliding scale $10-$25"
  /sliding\s+scale\s+\$?\d+\s*[-\u2013]\s*\$?\d+/gi,
  // "donation" / "suggested donation"
  /\b(?:suggested\s+)?donation(?:\s+\$\d+(?:\.\d{2})?)?/gi,
];

// URL patterns
const URL_PATTERN = /(?:https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b(?:[-a-zA-Z0-9@:%_+.~#?&/=]*)/gi;

// Email pattern
const EMAIL_PATTERN = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

// Phone patterns
const PHONE_PATTERNS = [
  /\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g,
  /\d{3}[-.\s]\d{3}[-.\s]\d{4}/g,
];

// Address: line with zip code or common street suffixes
const STREET_SUFFIXES = 'st(?:reet)?|ave(?:nue)?|blvd|boulevard|rd|road|dr(?:ive)?|ln|lane|way|ct|court|pkwy|parkway|pl(?:ace)?|cir(?:cle)?|hwy|highway|ter(?:race)?';
const ADDRESS_PATTERNS = [
  // "4200 River Road, Austin TX 78702"
  new RegExp(`\\d+\\s+[A-Za-z].*(?:${STREET_SUFFIXES})\\.?\\b.*\\d{5}(?:-\\d{4})?`, 'gi'),
  // "4200 River Road, Austin TX"
  new RegExp(`\\d+\\s+[A-Za-z].*(?:${STREET_SUFFIXES})\\.?\\b.*,\\s*[A-Za-z]+(?:\\s+[A-Z]{2})?`, 'gi'),
  // Line with a 5-digit zip
  /^.*\b\d{5}(?:-\d{4})?\s*$/gm,
];

// Age restriction patterns
const AGE_PATTERNS = [
  /\b(?:all\s+ages?|18\+|21\+|ages?\s+\d+\+?|(?:no\s+)?age\s+(?:limit|restriction)|minors?\s+(?:welcome|allowed)|(?:kids|children)\s+(?:under|below)\s+\d+\s+free)\b/gi,
];

// Venue indicator words
const VENUE_WORDS = /\b(?:park|hall|church|bar|club|theater|theatre|centre|center|room|pavilion|gallery|studio|gym|field|arena|auditorium|ballroom|lounge|brewery|winery|rooftop|warehouse|tavern|pub|cafe|restaurant|amphitheater|coliseum|stadium|temple|lodge|library|museum|commons|plaza|garden|yard|lot|deck|patio|terrace|stage|lawn)\b/i;

// Greeting patterns to skip when detecting title
const GREETINGS = /^(?:hey\s+(?:everyone|all|y'?all|folks|there|guys)|hi\s+(?:everyone|all|there|folks)|hello|greetings|what'?s?\s+up|yo|attention|reminder|fyi|heads?\s+up|check\s+(?:this|it)\s+out|exciting\s+news|announcement|save\s+the\s+date)[!:,.]*$/i;

/**
 * Extract all patterns from text -- Layer 1.
 * Returns structured data with confidence scores.
 */
function extractPatterns(text) {
  const result = {
    dates: [],
    times: [],
    prices: [],
    urls: [],
    emails: [],
    phones: [],
    addresses: [],
    ages: [],
    // Track which parts of text were consumed by pattern matching
    _consumed: new Set(),
  };

  const lines = text.split('\n');

  // -- Dates --
  for (const pattern of DATE_PATTERNS) {
    pattern.lastIndex = 0;
    let m;
    while ((m = pattern.exec(text)) !== null) {
      const raw = m[0].trim();
      if (!result.dates.some(d => d.raw === raw)) {
        result.dates.push({ raw, index: m.index, confidence: 'high' });
        markConsumed(result._consumed, m.index, m[0].length, lines, text);
      }
    }
  }

  // -- Times --
  for (const pattern of TIME_PATTERNS) {
    pattern.lastIndex = 0;
    let m;
    while ((m = pattern.exec(text)) !== null) {
      const raw = m[0].trim();
      if (!result.times.some(t => t.raw === raw)) {
        result.times.push({ raw, index: m.index, confidence: 'high' });
        markConsumed(result._consumed, m.index, m[0].length, lines, text);
      }
    }
  }

  // -- Prices --
  for (const pattern of PRICE_PATTERNS) {
    pattern.lastIndex = 0;
    let m;
    while ((m = pattern.exec(text)) !== null) {
      const raw = m[0].trim();
      if (!result.prices.some(p => p.raw === raw)) {
        result.prices.push({ raw, index: m.index, confidence: 'high' });
        markConsumed(result._consumed, m.index, m[0].length, lines, text);
      }
    }
  }

  // -- URLs --
  URL_PATTERN.lastIndex = 0;
  let um;
  while ((um = URL_PATTERN.exec(text)) !== null) {
    const raw = um[0].trim();
    // Exclude email-like matches (already caught by email pattern)
    if (!raw.includes('@') && !result.urls.some(u => u.raw === raw)) {
      result.urls.push({ raw, index: um.index, confidence: 'high' });
      markConsumed(result._consumed, um.index, um[0].length, lines, text);
    }
  }

  // -- Emails --
  EMAIL_PATTERN.lastIndex = 0;
  let em;
  while ((em = EMAIL_PATTERN.exec(text)) !== null) {
    result.emails.push({ raw: em[0], index: em.index, confidence: 'high' });
    markConsumed(result._consumed, em.index, em[0].length, lines, text);
  }

  // -- Phones --
  for (const pattern of PHONE_PATTERNS) {
    pattern.lastIndex = 0;
    let pm;
    while ((pm = pattern.exec(text)) !== null) {
      const raw = pm[0].trim();
      if (!result.phones.some(p => p.raw === raw)) {
        result.phones.push({ raw, index: pm.index, confidence: 'high' });
        markConsumed(result._consumed, pm.index, pm[0].length, lines, text);
      }
    }
  }

  // -- Addresses --
  for (const pattern of ADDRESS_PATTERNS) {
    pattern.lastIndex = 0;
    let am;
    while ((am = pattern.exec(text)) !== null) {
      const raw = am[0].trim();
      if (!result.addresses.some(a => a.raw === raw)) {
        result.addresses.push({ raw, index: am.index, confidence: 'high' });
        markConsumed(result._consumed, am.index, am[0].length, lines, text);
      }
    }
  }

  // -- Age restrictions --
  for (const pattern of AGE_PATTERNS) {
    pattern.lastIndex = 0;
    let agm;
    while ((agm = pattern.exec(text)) !== null) {
      result.ages.push({ raw: agm[0].trim(), index: agm.index, confidence: 'high' });
      markConsumed(result._consumed, agm.index, agm[0].length, lines, text);
    }
  }

  return result;
}

/**
 * Mark line indices as consumed by pattern extraction.
 */
function markConsumed(consumed, charIndex, length, lines, fullText) {
  // Find which line(s) this match spans
  let lineStart = 0;
  for (let i = 0; i < lines.length; i++) {
    const lineEnd = lineStart + lines[i].length;
    if (charIndex >= lineStart && charIndex < lineEnd + 1) {
      consumed.add(i);
    }
    lineStart = lineEnd + 1; // +1 for \n
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// LAYER 2: POSITIONAL HEURISTICS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Apply heuristics to remaining (unconsumed) lines to extract title,
 * venue, and description.
 */
function applyHeuristics(text, patterns) {
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const consumed = patterns._consumed;

  const result = {
    title: null,
    venue: null,
    description: null,
  };

  // Find unconsumed, non-empty lines
  const unconsumed = [];
  const allLines = text.split('\n');
  for (let i = 0; i < allLines.length; i++) {
    const line = allLines[i].trim();
    if (line.length > 0 && !consumed.has(i)) {
      unconsumed.push({ text: line, index: i });
    }
  }

  // -- Title detection --
  // Priority: ALL CAPS short line > first non-greeting short line
  let titleCandidate = null;

  // Look for ALL CAPS lines (common for event titles)
  const capsLines = unconsumed.filter(l =>
    l.text.length > 3 &&
    l.text.length < 80 &&
    l.text === l.text.toUpperCase() &&
    /[A-Z]/.test(l.text) &&
    !GREETINGS.test(l.text) &&
    !VENUE_WORDS.test(l.text)
  );
  if (capsLines.length > 0) {
    titleCandidate = capsLines[0];
  }

  // Fallback: first non-greeting, non-venue line under 60 chars
  if (!titleCandidate) {
    for (const line of unconsumed) {
      if (line.text.length > 2 && line.text.length < 80 &&
          !GREETINGS.test(line.text) &&
          !VENUE_WORDS.test(line.text)) {
        titleCandidate = line;
        break;
      }
    }
  }

  if (titleCandidate) {
    // Clean up common prefixes
    let titleText = titleCandidate.text
      .replace(/^(?:re:\s*|fwd?:\s*|subject:\s*)/i, '')
      .replace(/[!]+$/, '!')
      .trim();
    result.title = { raw: titleText, confidence: 'medium', index: titleCandidate.index };
    consumed.add(titleCandidate.index);
  }

  // -- Venue detection --
  // Lines containing venue-indicator words, or lines adjacent to an address
  const addressLineIndices = new Set();
  for (const addr of patterns.addresses) {
    // Find line index of address
    let charPos = 0;
    for (let i = 0; i < allLines.length; i++) {
      if (addr.index >= charPos && addr.index < charPos + allLines[i].length + 1) {
        addressLineIndices.add(i);
        break;
      }
      charPos += allLines[i].length + 1;
    }
  }

  // Check unconsumed lines for venue words
  for (const line of unconsumed) {
    if (consumed.has(line.index)) continue;
    if (line.index === titleCandidate?.index) continue;

    const isVenueWord = VENUE_WORDS.test(line.text);
    const isAdjacentToAddress = addressLineIndices.has(line.index - 1) ||
                                 addressLineIndices.has(line.index + 1);

    if ((isVenueWord || isAdjacentToAddress) && line.text.length < 80) {
      result.venue = { raw: line.text, confidence: isVenueWord ? 'high' : 'medium', index: line.index };
      consumed.add(line.index);
      break;
    }
  }

  // -- Description --
  // Whatever is left after all extractions, concatenated
  const remaining = [];
  for (let i = 0; i < allLines.length; i++) {
    const line = allLines[i].trim();
    if (line.length > 0 && !consumed.has(i)) {
      // Skip lines that are just greetings or sign-offs
      if (GREETINGS.test(line)) continue;
      if (/^(?:thanks|thank\s+you|cheers|best|regards|see\s+you|hope\s+to|looking\s+forward|xo+|<3)[!.,]*$/i.test(line)) continue;
      remaining.push(line);
    }
  }

  if (remaining.length > 0) {
    const desc = remaining.join('. ').substring(0, 200);
    result.description = { raw: desc, confidence: 'low' };
  }

  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PARSER
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parse unstructured text and extract structured event fields.
 *
 * @param {string} rawText -- pasted text (may contain HTML)
 * @returns {object} parsed fields:
 *   { title, date, time, venue, address, description, price, url, email, phone, age }
 *   Each field: { value: string, confidence: 'high'|'medium'|'low' } or null
 */
export function parseEventText(rawText) {
  if (!rawText || typeof rawText !== 'string') {
    return emptyResult();
  }

  // Truncate extremely long input
  const text = stripHtml(rawText).substring(0, 5000);

  if (text.trim().length === 0) {
    return emptyResult();
  }

  // Layer 1: Pattern extraction
  const patterns = extractPatterns(text);

  // Layer 2: Positional heuristics
  const heuristics = applyHeuristics(text, patterns);

  // -- Assemble final result --
  const result = {
    title: null,
    date: null,
    time: null,
    venue: null,
    address: null,
    description: null,
    price: null,
    url: null,
    email: null,
    phone: null,
    age: null,
  };

  // Title
  if (heuristics.title) {
    result.title = { value: heuristics.title.raw, confidence: heuristics.title.confidence };
  }

  // Date -- pick the most complete one
  if (patterns.dates.length > 0) {
    // Prefer the longest match (most complete date)
    const best = patterns.dates.sort((a, b) => b.raw.length - a.raw.length)[0];
    result.date = { value: best.raw, confidence: best.confidence };
  }

  // Time -- combine range if multiple
  if (patterns.times.length > 0) {
    const timeStr = patterns.times.map(t => t.raw).join(' ');
    // Deduplicate if the range was captured multiple times
    const cleaned = deduplicateTime(timeStr);
    result.time = { value: cleaned, confidence: patterns.times[0].confidence };
  }

  // Venue
  if (heuristics.venue) {
    result.venue = { value: heuristics.venue.raw, confidence: heuristics.venue.confidence };
  }

  // Address
  if (patterns.addresses.length > 0) {
    result.address = { value: patterns.addresses[0].raw, confidence: patterns.addresses[0].confidence };
  }

  // Price -- pick the most descriptive
  if (patterns.prices.length > 0) {
    const best = patterns.prices.sort((a, b) => b.raw.length - a.raw.length)[0];
    result.price = { value: best.raw, confidence: best.confidence };
  }

  // URL -- pick the first one (most likely to be the main link)
  if (patterns.urls.length > 0) {
    result.url = { value: patterns.urls[0].raw, confidence: patterns.urls[0].confidence };
  }

  // Email
  if (patterns.emails.length > 0) {
    result.email = { value: patterns.emails[0].raw, confidence: patterns.emails[0].confidence };
  }

  // Phone
  if (patterns.phones.length > 0) {
    result.phone = { value: patterns.phones[0].raw, confidence: patterns.phones[0].confidence };
  }

  // Age restriction
  if (patterns.ages.length > 0) {
    result.age = { value: patterns.ages[0].raw.toUpperCase(), confidence: patterns.ages[0].confidence };
  }

  // Description
  if (heuristics.description) {
    result.description = { value: heuristics.description.raw, confidence: heuristics.description.confidence };
  }

  return result;
}

/**
 * Deduplicate time strings that might contain the same range captured twice.
 */
function deduplicateTime(timeStr) {
  // Simple dedup: if the string contains the same time twice, keep just one
  const parts = timeStr.split(/\s+/);
  const seen = new Set();
  const deduped = [];
  for (const p of parts) {
    const normalized = p.toLowerCase().replace(/[,]/g, '');
    if (!seen.has(normalized)) {
      seen.add(normalized);
      deduped.push(p);
    }
  }
  return deduped.join(' ');
}

function emptyResult() {
  return {
    title: null, date: null, time: null, venue: null, address: null,
    description: null, price: null, url: null, email: null, phone: null, age: null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// TEMPLATE FIELD MAPPING
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Map parsed fields to template object roles.
 * Returns a map of { objectId: newText } for objects on the canvas.
 *
 * @param {object} parsed -- from parseEventText()
 * @param {Array} templateObjects -- template.objects array
 * @returns {object} { textEdits: {id: text}, unmapped: [{label, value}] }
 */
export function mapToTemplate(parsed, templateObjects) {
  const textEdits = {};
  const unmapped = [];
  const mappedFields = new Set();

  // Role -> parsed field mapping
  const ROLE_MAP = {
    'title':          'title',
    'headline':       'title',
    'event-name':     'title',
    'date':           'date',
    'event-date':     'date',
    'time':           'time',
    'event-time':     'time',
    'venue':          'venue',
    'event-venue':    'venue',
    'location':       'venue',
    'detail':         'address',
    'address':        'address',
    'event-address':  'address',
    'admission':      'price',
    'price':          'price',
    'cost':           'price',
    'info':           'age',
    'description':    'description',
    'body':           'description',
    'details':        'description',
    'url':            'url',
    'link':           'url',
    'contact':        'email',
  };

  // Map each template object by its role
  for (const obj of templateObjects) {
    if (obj.type !== 'text') continue;
    const role = obj.role || obj.id;
    const fieldName = ROLE_MAP[role];

    if (fieldName && parsed[fieldName]?.value) {
      textEdits[obj.id] = parsed[fieldName].value;
      mappedFields.add(fieldName);
    }
  }

  // Also try mapping by object ID directly (some templates use IDs as roles)
  for (const obj of templateObjects) {
    if (obj.type !== 'text') continue;
    if (textEdits[obj.id]) continue; // already mapped

    const id = obj.id;
    const fieldName = ROLE_MAP[id];
    if (fieldName && parsed[fieldName]?.value && !mappedFields.has(fieldName)) {
      textEdits[obj.id] = parsed[fieldName].value;
      mappedFields.add(fieldName);
    }
  }

  // Collect unmapped extracted fields
  for (const [field, data] of Object.entries(parsed)) {
    if (data?.value && !mappedFields.has(field)) {
      const label = field.charAt(0).toUpperCase() + field.slice(1);
      unmapped.push({ label, value: data.value, field });
    }
  }

  return { textEdits, unmapped };
}

/**
 * Get the list of fields the parser can extract, for display in the UI.
 */
export function getFieldLabels() {
  return {
    title: 'Title',
    date: 'Date',
    time: 'Time',
    venue: 'Venue',
    address: 'Address',
    description: 'Description',
    price: 'Price / Admission',
    url: 'URL / Link',
    email: 'Email',
    phone: 'Phone',
    age: 'Age / Restriction',
  };
}
